import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { Signer } from '@aws-amplify/core';
import { captureException } from '@sentry/react';

import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';
import type {
  AttachSpendRulesToGroupCardsInput,
  AttachSpendRulesToGroupCardsPayload,
  QueryGetCardsArgs,
  QueryGetCardsLiteArgs,
  QueryGetCardsManagementFilterOptionsArgs,
  QueryListMerchantSpendRulesForPaidolArgs,
  QueryV2ListCardGroupsByPaidolIdArgs,
  QueryV2ListProjectGroupsByPaidolIdArgs,
  QueryGetBudgetSpendByCurrentCycleArgs,
  MutationIssuePaymentCardArgs,
  MutationAssignCardArgs,
  MutationAddCardsToCardGroupArgs,
  MutationRemoveCardGroupArgs,
  MutationAddMerchantSpendRuleArgs,
  MutationEditMerchantSpendRuleArgs,
  MutationRemoveMerchantSpendRuleArgs,
  MutationLockUnlockPaymentCardArgs,
  GetCardsPayloadResult,
  GetCardsLitePayloadResult,
  GetCardsManagementFilterOptionsResult,
  MerchantSpendRule,
  BudgetSpendByCurrentCycle,
  V2ListCardGroupsByPaidolIdPayload,
  V2ListProjectGroupsByPaidolIdPayload,
  CardGroupSortInput,
  CardGroupFilterInput,
  ProjectGroupSortInput,
  ProjectGroupFilterInput,
  IssuePaymentCardResponse,
  PaidolUserPayload,
  BasicHttpResponse,
  EditMerchantSpendRuleResult,
  LockUnlockPaymentCardPayload,
} from 'CDK_API';

/**
 * Relaxes all properties for the Amplify→CDK boundary:
 * - All fields become optional and nullable
 * - String literal unions (enums) are widened to string
 * - Recursively applies to nested objects and InputMaybe<T> unions
 * AppSync validates the actual GraphQL types at runtime.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeepPartial<T> = T extends any[]
  ? T
  : T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> | null }
  : T extends string
  ? string | null
  : T | null;

// --- GraphQL Operations ---
// Query/mutation strings use typed arguments and field selections matching CDK AppSync schema.
// Selection sets are copied verbatim from the current frontend operation definitions so the
// return shapes stay identical to what the UI consumes today.

// Queries (8)

// Source: src/app/pages/store/queries.ts (getCards)
const getCardsQuery = /* GraphQL */ `
  query GetCards($input: GetCardsInput!) {
    getCards(input: $input) {
      nextToken
      total
      items {
        paymentCardId
        paidolId
        formFactor
        name
        last4
        expirationDate
        status
        procoreEmployee {
          id
          name
          email_address
        }
        cardholder {
          id
          name
          email
          picture
        }
        cardGroup {
          id
          name
        }
        spxSpendRule {
          id
          name
        }
        monthlySpendLimit {
          id
          name
          amount
          currency
          cumulativeRuleId
        }
        maximumTransactionAmount {
          id
          amount
          currency
        }
        spent {
          value
          currencyCode
        }
        isAdminLocked
        isAuthorizedUserCard
      }
    }
  }
`;

// Source: src/app/pages/store/queries.ts (getCardsLite)
const getCardsLiteQuery = /* GraphQL */ `
  query GetCardsLite($input: GetCardsInput!) {
    getCardsLite(input: $input) {
      nextToken
      total
      items {
        paymentCardId
        last4
        name
        cardholder {
          name
          picture
        }
        monthlySpendLimit {
          name
          amount
        }
      }
    }
  }
`;

// Source: src/graphql/queries.ts (getCardsManagementFilterOptions)
const getCardsManagementFilterOptionsQuery = /* GraphQL */ `
  query GetCardsManagementFilterOptions($input: GetCardsManagementFilterOptionsInput!) {
    getCardsManagementFilterOptions(input: $input) {
      cardGroupId {
        id
        label
        projectId
        __typename
      }
      cardNames {
        id
        label
        projectId
        __typename
      }
      cardholders {
        id
        label
        projectId
        __typename
      }
      last4 {
        id
        label
        projectId
        __typename
      }
      status {
        id
        label
        projectId
        __typename
      }
      __typename
    }
  }
`;

// Source: src/graphql/queries.ts (listMerchantSpendRulesForPaidol)
const listMerchantSpendRulesForPaidolQuery = /* GraphQL */ `
  query ListMerchantSpendRulesForPaidol($input: ListMerchantSpendRulesInput!) {
    listMerchantSpendRulesForPaidol(input: $input) {
      id
      paidolId
      authPaidolId
      name
      paymentCards
      merchantCountrySpendRuleId
      merchantCategorySpendRuleId
      amountLimitSpendRuleId
      monthlyLimitSpendRuleId
      monthlyLimitCumulativeRuleId
      maximumAmount {
        value
        currencyCode
        decimalPlaces
        __typename
      }
      monthlyLimitAmount {
        value
        currencyCode
        decimalPlaces
        __typename
      }
      merchantCategoryAllowed
      merchantCategoryBlocked
      merchantCountryAllowed
      merchantCountryBlocked
      monthlyLimitResetTimestamp
      createdAt
      updatedAt
      __typename
    }
  }
`;

// Source: src/graphql_custom/card-groups/queries.ts (v2GetGroupsForPaidolId)
const v2ListCardGroupsByPaidolIdQuery = /* GraphQL */ `
  query v2ListCardGroupsByPaidolId(
    $paidolId: String!
    $nextToken: String
    $limit: Int = 100
    $sort: CardGroupSortInput
    $filter: CardGroupFilterInput
  ) {
    v2ListCardGroupsByPaidolId(
      input: {
        limit: $limit
        nextToken: $nextToken
        filter: { paidolId: { eq: $paidolId } }
        sort: $sort
        advancedFilter: $filter
      }
    ) {
      nextToken
      total
      items {
        id
        name
        createdAt
        updatedAt
        spendGroupId
        authPaidolId
        groupProjects
        isMonthlyBudgetUnlimited
        paymentCards(limit: $limit) {
          items {
            paymentCardId
            last4
            paidolUsers {
              items {
                paidolUser {
                  id
                  email
                  user {
                    id
                    first_name
                    last_name
                    picture
                  }
                }
              }
            }
          }
        }
        groupAdmins {
          items {
            approvalLevels
            isReviewer
            paidolUser {
              id
              email
              user {
                id
                first_name
                last_name
                picture
              }
            }
          }
        }
        monthlyBudget {
          currency
          value
        }
        spentThisMonth {
          yearAndMonth
          amount
        }
        glCodeVisibilitySettings {
          isAllAvailable
          ids
        }
        projectVisibilitySettings {
          isAllAvailable
          ids
        }
      }
    }
  }
`;

// Source: src/graphql_custom/project-groups/queries.ts (v2GetProjectGroupsForPaidolId)
const v2ListProjectGroupsByPaidolIdQuery = /* GraphQL */ `
  query v2ListProjectGroupsByPaidolId(
    $paidolId: String!
    $nextToken: String
    $limit: Int = 100
    $sort: ProjectGroupSortInput
    $filter: ProjectGroupFilterInput
  ) {
    v2ListProjectGroupsByPaidolId(
      input: {
        limit: $limit
        nextToken: $nextToken
        filter: { paidolId: { eq: $paidolId } }
        sort: $sort
        advancedFilter: $filter
      }
    ) {
      nextToken
      total
      items {
        id
        name
        createdAt
        updatedAt
        spendGroupId
        authPaidolId
        groupProjects
        isMonthlyBudgetUnlimited
        paymentCards(limit: $limit) {
          items {
            paymentCardId
            paidolUsers {
              items {
                paidolUser {
                  id
                  email
                  user {
                    id
                    first_name
                    last_name
                    picture
                  }
                }
              }
            }
          }
        }
        groupAdmins {
          items {
            approvalLevels
            isReviewer
            paidolUser {
              id
              email
              user {
                id
                first_name
                last_name
                picture
              }
            }
          }
        }
        monthlyBudget {
          currency
          value
        }
        spentThisMonth {
          yearAndMonth
          amount
        }
      }
    }
  }
`;

// Source: src/app/pages/store/queries.ts — extracted from the batched
// GetPaymentCardLocalDetails query (getBudgetSpendByCurrentCycle selection only).
const getBudgetSpendByCurrentCycleQuery = /* GraphQL */ `
  query GetBudgetSpendByCurrentCycle($paymentCardId: ID!) {
    getBudgetSpendByCurrentCycle(paymentCardId: $paymentCardId) {
      paymentCardId
      amount
      transactionCount
      processedTransactionIds
    }
  }
`;

// Source: src/graphql/queries.ts (v2CardHealthCheck)
const v2CardHealthCheckQuery = /* GraphQL */ `
  query V2CardHealthCheck($input: String) {
    v2CardHealthCheck(input: $input)
  }
`;

// Mutations (8)

// Source: src/graphql_custom/cards/mutations.ts (issuePaymentCard)
const issuePaymentCardMutation = /* GraphQL */ `
  mutation IssuePaymentCard($input: IssuePaymentCard!) {
    issuePaymentCard(input: $input) {
      statusCode
      errorMessage
      errorType
      paymentCardId
    }
  }
`;

// Source: src/graphql/mutations.ts (assignCard)
const assignCardMutation = /* GraphQL */ `
  mutation AssignCard($input: InviteUserToPaidol!, $hostname: String!, $highnotePaymentCardId: String) {
    assignCard(input: $input, hostname: $hostname, highnotePaymentCardId: $highnotePaymentCardId) {
      id
      paidol_id
      email
      roles
      user_id
      employee_id
      authorizedUserId
      authorizedUserApplicationId
      highnotePaymentCardId
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

// Source: src/app/services/mutations.ts (addCardsToCardGroup)
const addCardsToCardGroupMutation = /* GraphQL */ `
  mutation AddCardsToCardGroup($input: AddCardsToCardGroupInput!) {
    addCardsToCardGroup(input: $input) {
      body
      statusCode
    }
  }
`;

// Source: src/graphql_custom/card-groups/mutations.ts (removeCardGroup)
const removeCardGroupMutation = /* GraphQL */ `
  mutation RemoveCardGroup($input: RemoveCardGroupInput!) {
    removeCardGroup(input: $input) {
      statusCode
      body
      __typename
    }
  }
`;

// SEE-193: re-applies the card group's spend rule to its cards, served by the CDK
// attach-spend-rules-to-group-cards Lambda. The UI ignores the response body
// (groupsSlice only clears its loading flag), so the selection stays minimal.
const attachSpendRulesToGroupCardsMutation = /* GraphQL */ `
  mutation AttachSpendRulesToGroupCards($input: AttachSpendRulesToGroupCardsInput!) {
    attachSpendRulesToGroupCards(input: $input) {
      cardGroupId
      cardGroup {
        id
        spendGroupId
      }
    }
  }
`;

// Source: src/graphql/mutations.ts (addMerchantSpendRule)
const addMerchantSpendRuleMutation = /* GraphQL */ `
  mutation AddMerchantSpendRule($input: AddMerchantSpendRuleInput!) {
    addMerchantSpendRule(input: $input) {
      id
      paidolId
      authPaidolId
      name
      paymentCards
      merchantCountrySpendRuleId
      merchantCategorySpendRuleId
      amountLimitSpendRuleId
      monthlyLimitSpendRuleId
      monthlyLimitCumulativeRuleId
      maximumAmount {
        value
        currencyCode
        decimalPlaces
        __typename
      }
      monthlyLimitAmount {
        value
        currencyCode
        decimalPlaces
        __typename
      }
      merchantCategoryAllowed
      merchantCategoryBlocked
      merchantCountryAllowed
      merchantCountryBlocked
      monthlyLimitResetTimestamp
      createdAt
      updatedAt
      __typename
    }
  }
`;

// Source: src/graphql/mutations.ts (editMerchantSpendRule)
const editMerchantSpendRuleMutation = /* GraphQL */ `
  mutation EditMerchantSpendRule($input: EditMerchantSpendRuleInput!) {
    editMerchantSpendRule(input: $input) {
      __typename
      ... on MerchantSpendRule {
        id
        paidolId
        authPaidolId
        name
        paymentCards
        merchantCountrySpendRuleId
        merchantCategorySpendRuleId
        amountLimitSpendRuleId
        monthlyLimitSpendRuleId
        monthlyLimitCumulativeRuleId
        maximumAmount {
          value
          currencyCode
          decimalPlaces
          __typename
        }
        monthlyLimitAmount {
          value
          currencyCode
          decimalPlaces
          __typename
        }
        merchantCategoryAllowed
        merchantCategoryBlocked
        merchantCountryAllowed
        merchantCountryBlocked
        monthlyLimitResetTimestamp
        createdAt
        updatedAt
      }
      ... on MerchantSpendRuleError {
        errors
      }
    }
  }
`;

// Source: src/graphql/mutations.ts (removeMerchantSpendRule)
const removeMerchantSpendRuleMutation = /* GraphQL */ `
  mutation RemoveMerchantSpendRule($input: String!) {
    removeMerchantSpendRule(input: $input) {
      statusCode
      body
      __typename
    }
  }
`;

// Source: src/app/pages/store/queries.ts (LockUnlockPaymentCard)
const lockUnlockPaymentCardMutation = /* GraphQL */ `
  mutation LockUnlockPaymentCard($input: LockUnlockPaymentCardInput!) {
    lockUnlockPaymentCard(input: $input) {
      statusCode
      status
      paymentCardId
    }
  }
`;

// SEE-1232 row e. Source: graphql_custom/card-groups/queries.ts (listPaidolUserToCardGroups)
// and graphql_custom/project-groups/queries.ts (listPaidolUserToProjectGroups). CDK takes
// paidolId + the group id as top-level arguments instead of Amplify's `filter`, and rows
// belonging to another paidol are dropped server-side.
const listPaidolUserToCardGroupsQuery = /* GraphQL */ `
  query ListPaidolUserToCardGroups($paidolId: ID!, $cardGroupId: ID!, $limit: Int, $nextToken: String) {
    listPaidolUserToCardGroups(
      paidolId: $paidolId
      cardGroupId: $cardGroupId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        paidolId
        paidolUserId
        approvalLevels
        isReviewer
        paidolUser {
          id
          email
          user {
            id
            first_name
            last_name
          }
        }
        createdAt
        updatedAt
        cardGroupId
      }
      nextToken
    }
  }
`;

const listPaidolUserToProjectGroupsQuery = /* GraphQL */ `
  query ListPaidolUserToProjectGroups($paidolId: ID!, $projectGroupId: ID!, $limit: Int, $nextToken: String) {
    listPaidolUserToProjectGroups(
      paidolId: $paidolId
      projectGroupId: $projectGroupId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        paidolId
        paidolUserId
        approvalLevels
        isReviewer
        paidolUser {
          id
          email
          user {
            id
            first_name
            last_name
          }
        }
        createdAt
        updatedAt
        projectGroupId
      }
      nextToken
    }
  }
`;

// SEE-1232 row h — BudgetSpendByMonth* reads. Source: src/graphql/queries.ts (same names).
// Amounts are Int cents, exactly as on Amplify. The *ByAmount lists take `yearAndMonth`
// ("YYYY-MM"); the server applies the `YYYY-MM#` range prefix Amplify's
// `yearAndMonthAmount: { beginsWith }` expressed on the client.
const getBudgetSpendByMonthQuery = /* GraphQL */ `
  query GetBudgetSpendByMonth($paidolId: ID!, $yearAndMonth: String!) {
    getBudgetSpendByMonth(paidolId: $paidolId, yearAndMonth: $yearAndMonth) {
      paidolId
      authPaidolId
      yearAndMonth
      amount
      transactionCount
      createdAt
      updatedAt
    }
  }
`;

const getBudgetSpendByMonthAndPaymentCardQuery = /* GraphQL */ `
  query GetBudgetSpendByMonthAndPaymentCard($paidolId: ID!, $yearAndMonth: String!, $paymentCardId: ID!) {
    getBudgetSpendByMonthAndPaymentCard(
      paidolId: $paidolId
      yearAndMonth: $yearAndMonth
      paymentCardId: $paymentCardId
    ) {
      paidolId
      authPaidolId
      yearAndMonth
      paymentCardId
      amount
      transactionCount
      createdAt
      updatedAt
    }
  }
`;

const listBudgetSpendByMonthsQuery = /* GraphQL */ `
  query ListBudgetSpendByMonths(
    $paidolId: ID!
    $limit: Int
    $nextToken: String
    $sortDirection: SortDirection
  ) {
    listBudgetSpendByMonths(
      paidolId: $paidolId
      limit: $limit
      nextToken: $nextToken
      sortDirection: $sortDirection
    ) {
      items {
        paidolId
        authPaidolId
        yearAndMonth
        amount
        transactionCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const listBudgetSpendByMonthAndMerchantByAmountQuery = /* GraphQL */ `
  query ListBudgetSpendByMonthAndMerchantByAmount(
    $paidolId: ID!
    $yearAndMonth: String!
    $limit: Int
    $nextToken: String
    $sortDirection: SortDirection
  ) {
    listBudgetSpendByMonthAndMerchantByAmount(
      paidolId: $paidolId
      yearAndMonth: $yearAndMonth
      limit: $limit
      nextToken: $nextToken
      sortDirection: $sortDirection
    ) {
      items {
        paidolId
        authPaidolId
        yearAndMonth
        merchantName
        amount
        transactionCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const listBudgetSpendByMonthAndCategoryByAmountQuery = /* GraphQL */ `
  query ListBudgetSpendByMonthAndCategoryByAmount(
    $paidolId: ID!
    $yearAndMonth: String!
    $limit: Int
    $nextToken: String
    $sortDirection: SortDirection
  ) {
    listBudgetSpendByMonthAndCategoryByAmount(
      paidolId: $paidolId
      yearAndMonth: $yearAndMonth
      limit: $limit
      nextToken: $nextToken
      sortDirection: $sortDirection
    ) {
      items {
        paidolId
        authPaidolId
        yearAndMonth
        category
        amount
        transactionCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// With `jobCodeId` the CDK field is a point read returning 0 or 1 items. Amplify's
// `filter: { jobCodeId: { eq } }` never matched the CDK signature.
const listBudgetSpendByMonthAndJobCodeIdByAmountQuery = /* GraphQL */ `
  query ListBudgetSpendByMonthAndJobCodeIdByAmount($paidolId: ID!, $yearAndMonth: String!, $jobCodeId: ID) {
    listBudgetSpendByMonthAndJobCodeIdByAmount(
      paidolId: $paidolId
      yearAndMonth: $yearAndMonth
      jobCodeId: $jobCodeId
    ) {
      items {
        paidolId
        authPaidolId
        yearAndMonth
        jobCodeId
        amount
        transactionCount
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// SEE-1232 row b — CardGroup get / create / update. Sources: graphql_custom/card-groups/
// queries.ts (getCardGroupQuery) and mutations.ts (createCardGroup, updateCardGroup).
// CDK's CardGroup is the same enriched type v2ListCardGroupsByPaidolId already serves, so the
// selections follow it. The differences from the Amplify documents are re-keyed back in
// `toAmplifyCardGroupShape` below, so consumers see the shape they always did:
// - `monthlyBudgetSpend.items` is served as `spentThisMonth` (one current-month record).
// - `groupAdmins.items[].paidolUserId` is served as `paidolUser.id`.
// - `paymentCards` carries no `nextToken`; the sidesheet pages cards through fetchGroupCards.
// - `groupAdmins.items[].id` is the link row id GroupSidesheet keys its admin chips on. CDK
//   serves one admin per member (the lowest link id if a member has duplicate rows); the
//   re-key falls back to `paidolUser.id` only if a response ever lacks it.
// Not served, and read by nothing: the card-user link `id`, and `user.username` / `user.email`
// (the groups code reads `paidolUser.email`).
const getCardGroupQuery = /* GraphQL */ `
  query GetCardGroup($id: ID!, $cardLimit: Int = 100) {
    getCardGroup(id: $id) {
      __typename
      id
      paidolId
      name
      monthlyBudget {
        currency
        value
      }
      spentThisMonth {
        amount
        yearAndMonth
      }
      groupAdmins {
        items {
          id
          approvalLevels
          isReviewer
          paidolUser {
            id
            email
            user {
              id
              picture
              first_name
              last_name
            }
          }
        }
      }
      paymentCards(limit: $cardLimit) {
        items {
          paymentCardId
          paidolUsers {
            items {
              paidolUser {
                id
                email
                user {
                  id
                  picture
                  first_name
                  last_name
                }
              }
            }
          }
        }
      }
      glCodeVisibilitySettings {
        isAllAvailable
        ids
      }
      projectVisibilitySettings {
        isAllAvailable
        ids
      }
    }
  }
`;

// `authPaidolId` is derived server-side and is not an input; `$condition` is not supported.
const createCardGroupMutation = /* GraphQL */ `
  mutation CreateCardGroup($input: V2CreateCardGroupInput!) {
    createCardGroup(input: $input) {
      id
      name
      isMonthlyBudgetUnlimited
      spendGroupId
      paymentCards(limit: 100) {
        items {
          paymentCardId
        }
      }
      monthlyBudget {
        currency
        value
      }
      spentThisMonth {
        amount
        yearAndMonth
      }
      glCodeVisibilitySettings {
        isAllAvailable
        ids
      }
      projectVisibilitySettings {
        isAllAvailable
        ids
      }
    }
  }
`;

const updateCardGroupMutation = /* GraphQL */ `
  mutation UpdateCardGroup($input: V2UpdateCardGroupInput!) {
    updateCardGroup(input: $input) {
      id
      name
      isMonthlyBudgetUnlimited
      paymentCards(limit: 100) {
        items {
          paymentCardId
        }
      }
      monthlyBudget {
        currency
        value
      }
      spentThisMonth {
        amount
        yearAndMonth
      }
      glCodeVisibilitySettings {
        isAllAvailable
        ids
      }
      projectVisibilitySettings {
        isAllAvailable
        ids
      }
    }
  }
`;

interface CdkCardGroupShape {
  spentThisMonth?: { amount?: number | null; yearAndMonth?: string | null } | null;
  groupAdmins?: {
    items?: Array<{ id?: string | null; paidolUser?: { id?: string | null } | null } | null> | null;
  } | null;
  paymentCards?: { items?: unknown[] | null } | null;
  [key: string]: unknown;
}

/**
 * Re-keys the CDK CardGroup into the fields the Amplify documents selected, keeping the CDK
 * ones too (consumers already read `spentThisMonth` off the CDK list read).
 */
export const toAmplifyCardGroupShape = <T>(group: CdkCardGroupShape | null | undefined): T | undefined => {
  if (!group) return undefined;
  const spent = group.spentThisMonth;
  return {
    ...group,
    monthlyBudgetSpend: {
      items: spent ? [{ amount: spent.amount, yearAndMonth: spent.yearAndMonth }] : [],
    },
    ...(group.groupAdmins && {
      groupAdmins: {
        ...group.groupAdmins,
        items: (group.groupAdmins.items ?? []).map((admin) =>
          admin
            ? {
                ...admin,
                id: admin.id ?? admin.paidolUser?.id ?? null,
                paidolUserId: admin.paidolUser?.id ?? null,
              }
            : admin
        ),
      },
    }),
    ...(group.paymentCards && { paymentCards: { ...group.paymentCards, nextToken: null } }),
  } as unknown as T;
};

// SEE-1232 row f — the local HighnotePaymentCard record. Sources: app/pages/store/queries.ts
// (GetHighnotePaymentCard, GetPaymentCardLocalDetails, CreateHighnotePaymentCard). CDK's
// V2HighnotePaymentCardDetail serves every field those documents select, so they move
// verbatim; GetPaymentCardLocalDetails still batches the per-card budget read in one request.
// `paidolUsers(limit:)` is accepted but not applied — a card has a handful of links.
const getHighnotePaymentCardQuery = /* GraphQL */ `
  query GetHighnotePaymentCard($paymentCardId: ID!) {
    getHighnotePaymentCard(paymentCardId: $paymentCardId) {
      paymentCardId
      paidolId
      authPaidolId
      cardGroupId
      name
      last4
      formFactor
      status
      hasPin
      cardholderId
      cardholder {
        id
        defaultAgaveEmployeeId
        defaultAgaveEmployee {
          id
          name
        }
      }
      group {
        id
        name
      }
      paidolUsers(limit: 100) {
        items {
          id
          paidolUser {
            id
            email
            user {
              id
              username
              email
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

const getPaymentCardLocalDetailsQuery = /* GraphQL */ `
  query GetPaymentCardLocalDetails($paymentCardId: ID!, $paidolId: ID!, $yearAndMonth: String!) {
    getHighnotePaymentCard(paymentCardId: $paymentCardId) {
      paymentCardId
      paidolId
      authPaidolId
      cardGroupId
      name
      last4
      formFactor
      status
      hasPin
      cardholderId
      cardholder {
        id
        defaultAgaveEmployeeId
        defaultAgaveEmployee {
          id
          name
        }
      }
      group {
        id
        name
      }
      isAdminLocked
      lastActionByRole
      lastActionByPaidolUserId
      paidolUsers(limit: 100) {
        items {
          id
          paidolUser {
            id
            email
            user {
              id
              username
              email
              picture
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
    getBudgetSpendByMonthAndPaymentCard(
      paidolId: $paidolId
      yearAndMonth: $yearAndMonth
      paymentCardId: $paymentCardId
    ) {
      paidolId
      authPaidolId
      yearAndMonth
      paymentCardId
      amount
      transactionCount
      createdAt
      updatedAt
    }
  }
`;

// Backfills the local row when Highnote has a card the table lacks. CDK is stricter than
// Amplify here: it checks with Highnote that the card belongs to one of the company's
// business account holders (fails closed), and returns the existing row if it is already
// there. `authPaidolId` is derived server-side; there is no `$condition`.
const createHighnotePaymentCardMutation = /* GraphQL */ `
  mutation CreateHighnotePaymentCard($input: V2CreateHighnotePaymentCardInput!) {
    createHighnotePaymentCard(input: $input) {
      paymentCardId
      paidolId
      authPaidolId
      cardGroupId
      name
      group {
        name
      }
      paidolUsers(limit: 100) {
        items {
          id
          paidolUser {
            email
            user {
              id
              username
              email
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export interface PaymentCardLocalDetails<C, B> {
  getHighnotePaymentCard?: C | null;
  getBudgetSpendByMonthAndPaymentCard?: B | null;
}

export interface BudgetSpendListArgs {
  paidolId: string;
  limit?: number | null;
  nextToken?: string | null;
  sortDirection?: 'ASC' | 'DESC' | string | null;
}

export interface GroupAdminLinksPage<T> {
  items: T[];
  nextToken: string | null;
}

type AuthMode = 'cognito' | 'iam';

// --- Service ---

// Matches Amplify's "no Cognito session" errors only — other failures
// (network, refresh-token, misconfig) should surface so authenticated users
// aren't silently downgraded to IAM scope.
function isNoCognitoSessionError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /no current user|not authenticated|user is not authenticated/i.test(message);
}

/**
 * Service for v2Card operations via the CDK AppSync API.
 *
 * Uses fetch() + Cognito JWT to call the CDK-managed AppSync endpoint
 * (REACT_APP_CDK_APPSYNC_ENDPOINT). Query arguments are typed (matching the
 * CDK schema) and returns are the typed structured payloads produced by the
 * v2Card Lambda — no AWSJSON parsing needed.
 */
export class CardService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  // --- Queries ---

  async getCards(variables: DeepPartial<QueryGetCardsArgs>): Promise<GetCardsPayloadResult> {
    const result = await this.execute<{ getCards: GetCardsPayloadResult }>(
      getCardsQuery,
      variables,
      'getCards'
    );
    return result.getCards;
  }

  async getCardsLite(variables: DeepPartial<QueryGetCardsLiteArgs>): Promise<GetCardsLitePayloadResult> {
    const result = await this.execute<{ getCardsLite: GetCardsLitePayloadResult }>(
      getCardsLiteQuery,
      variables,
      'getCardsLite'
    );
    return result.getCardsLite;
  }

  async getCardsManagementFilterOptions(
    variables: DeepPartial<QueryGetCardsManagementFilterOptionsArgs>
  ): Promise<GetCardsManagementFilterOptionsResult> {
    const result = await this.execute<{
      getCardsManagementFilterOptions: GetCardsManagementFilterOptionsResult;
    }>(getCardsManagementFilterOptionsQuery, variables, 'getCardsManagementFilterOptions');
    return result.getCardsManagementFilterOptions;
  }

  async listMerchantSpendRulesForPaidol(
    variables: DeepPartial<QueryListMerchantSpendRulesForPaidolArgs>
  ): Promise<MerchantSpendRule[]> {
    const result = await this.execute<{
      listMerchantSpendRulesForPaidol: MerchantSpendRule[] | null;
    }>(listMerchantSpendRulesForPaidolQuery, variables, 'listMerchantSpendRulesForPaidol');
    return result.listMerchantSpendRulesForPaidol ?? [];
  }

  async v2ListCardGroupsByPaidolId(
    variables: DeepPartial<QueryV2ListCardGroupsByPaidolIdArgs> & {
      paidolId: string;
      nextToken?: string | null;
      limit?: number | null;
      sort?: CardGroupSortInput | null;
      filter?: CardGroupFilterInput | null;
    }
  ): Promise<V2ListCardGroupsByPaidolIdPayload> {
    const result = await this.execute<{
      v2ListCardGroupsByPaidolId: V2ListCardGroupsByPaidolIdPayload;
    }>(v2ListCardGroupsByPaidolIdQuery, variables, 'v2ListCardGroupsByPaidolId');
    return result.v2ListCardGroupsByPaidolId;
  }

  async v2ListProjectGroupsByPaidolId(
    variables: DeepPartial<QueryV2ListProjectGroupsByPaidolIdArgs> & {
      paidolId: string;
      nextToken?: string | null;
      limit?: number | null;
      sort?: ProjectGroupSortInput | null;
      filter?: ProjectGroupFilterInput | null;
    }
  ): Promise<V2ListProjectGroupsByPaidolIdPayload> {
    const result = await this.execute<{
      v2ListProjectGroupsByPaidolId: V2ListProjectGroupsByPaidolIdPayload;
    }>(v2ListProjectGroupsByPaidolIdQuery, variables, 'v2ListProjectGroupsByPaidolId');
    return result.v2ListProjectGroupsByPaidolId;
  }

  async getBudgetSpendByCurrentCycle(
    variables: DeepPartial<QueryGetBudgetSpendByCurrentCycleArgs>
  ): Promise<BudgetSpendByCurrentCycle | null> {
    const result = await this.execute<{
      getBudgetSpendByCurrentCycle: BudgetSpendByCurrentCycle | null;
    }>(getBudgetSpendByCurrentCycleQuery, variables, 'getBudgetSpendByCurrentCycle');
    return result.getBudgetSpendByCurrentCycle ?? null;
  }

  async v2CardHealthCheck(input: string): Promise<string> {
    const result = await this.execute<{ v2CardHealthCheck: string }>(
      v2CardHealthCheckQuery,
      { input },
      'v2CardHealthCheck'
    );
    return result.v2CardHealthCheck;
  }

  // --- Mutations ---

  async issuePaymentCard(
    variables: DeepPartial<MutationIssuePaymentCardArgs>
  ): Promise<IssuePaymentCardResponse> {
    const result = await this.execute<{ issuePaymentCard: IssuePaymentCardResponse }>(
      issuePaymentCardMutation,
      variables,
      'issuePaymentCard'
    );
    return result.issuePaymentCard;
  }

  async assignCard(variables: DeepPartial<MutationAssignCardArgs>): Promise<PaidolUserPayload | null> {
    const result = await this.execute<{ assignCard: PaidolUserPayload | null }>(
      assignCardMutation,
      variables,
      'assignCard'
    );
    return result.assignCard ?? null;
  }

  async addCardsToCardGroup(
    variables: DeepPartial<MutationAddCardsToCardGroupArgs>
  ): Promise<BasicHttpResponse> {
    const result = await this.execute<{ addCardsToCardGroup: BasicHttpResponse }>(
      addCardsToCardGroupMutation,
      variables,
      'addCardsToCardGroup'
    );
    return result.addCardsToCardGroup;
  }

  /**
   * SEE-193: re-apply a card group's spend rule to every card in the group.
   * No-ops server-side when the group has no spendGroupId (the common case
   * since group spend rules left the UI in Nov 2025).
   */
  async attachSpendRulesToGroupCards(
    input: AttachSpendRulesToGroupCardsInput
  ): Promise<AttachSpendRulesToGroupCardsPayload | null> {
    const result = await this.execute<{
      attachSpendRulesToGroupCards: AttachSpendRulesToGroupCardsPayload | null;
    }>(attachSpendRulesToGroupCardsMutation, { input }, 'attachSpendRulesToGroupCards');
    return result.attachSpendRulesToGroupCards;
  }

  async removeCardGroup(variables: DeepPartial<MutationRemoveCardGroupArgs>): Promise<BasicHttpResponse> {
    const result = await this.execute<{ removeCardGroup: BasicHttpResponse }>(
      removeCardGroupMutation,
      variables,
      'removeCardGroup'
    );
    return result.removeCardGroup;
  }

  /**
   * SEE-1232 row e — group-admin join-table reads. One page per call; callers loop on
   * `nextToken` exactly as they did against Amplify.
   */
  async listPaidolUserToCardGroups<T>(variables: {
    paidolId: string;
    cardGroupId: string;
    limit?: number;
    nextToken?: string | null;
  }): Promise<GroupAdminLinksPage<T>> {
    const result = await this.execute<{ listPaidolUserToCardGroups: GroupAdminLinksPage<T> | null }>(
      listPaidolUserToCardGroupsQuery,
      variables,
      'listPaidolUserToCardGroups'
    );
    return {
      items: result.listPaidolUserToCardGroups?.items ?? [],
      nextToken: result.listPaidolUserToCardGroups?.nextToken ?? null,
    };
  }

  async listPaidolUserToProjectGroups<T>(variables: {
    paidolId: string;
    projectGroupId: string;
    limit?: number;
    nextToken?: string | null;
  }): Promise<GroupAdminLinksPage<T>> {
    const result = await this.execute<{ listPaidolUserToProjectGroups: GroupAdminLinksPage<T> | null }>(
      listPaidolUserToProjectGroupsQuery,
      variables,
      'listPaidolUserToProjectGroups'
    );
    return {
      items: result.listPaidolUserToProjectGroups?.items ?? [],
      nextToken: result.listPaidolUserToProjectGroups?.nextToken ?? null,
    };
  }

  /** SEE-1232 row f — the local HighnotePaymentCard record (null for an unknown card). */
  async getHighnotePaymentCard<T>(paymentCardId: string): Promise<T | null> {
    const result = await this.execute<{ getHighnotePaymentCard: T | null }>(
      getHighnotePaymentCardQuery,
      { paymentCardId },
      'getHighnotePaymentCard'
    );
    return result.getHighnotePaymentCard;
  }

  /** The card record plus its budget for the month, in one request (as on Amplify). */
  async getPaymentCardLocalDetails<C, B>(variables: {
    paymentCardId: string;
    paidolId: string;
    yearAndMonth: string;
  }): Promise<PaymentCardLocalDetails<C, B>> {
    return this.execute<PaymentCardLocalDetails<C, B>>(
      getPaymentCardLocalDetailsQuery,
      variables,
      'getPaymentCardLocalDetails'
    );
  }

  async createHighnotePaymentCard<T>(input: { paymentCardId: string; paidolId: string }): Promise<T | null> {
    const result = await this.execute<{ createHighnotePaymentCard: T | null }>(
      createHighnotePaymentCardMutation,
      { input },
      'createHighnotePaymentCard'
    );
    return result.createHighnotePaymentCard;
  }

  /** SEE-1232 row b — CardGroup get / create / update, re-keyed to the Amplify shape. */
  async getCardGroup<T>(variables: { id: string; cardLimit?: number }): Promise<T | undefined> {
    const result = await this.execute<{ getCardGroup: CdkCardGroupShape | null }>(
      getCardGroupQuery,
      variables,
      'getCardGroup'
    );
    return toAmplifyCardGroupShape<T>(result.getCardGroup);
  }

  async createCardGroup<T>(input: Record<string, unknown>): Promise<T | undefined> {
    const result = await this.execute<{ createCardGroup: CdkCardGroupShape | null }>(
      createCardGroupMutation,
      { input },
      'createCardGroup'
    );
    return toAmplifyCardGroupShape<T>(result.createCardGroup);
  }

  async updateCardGroup<T>(input: Record<string, unknown>): Promise<T | undefined> {
    const result = await this.execute<{ updateCardGroup: CdkCardGroupShape | null }>(
      updateCardGroupMutation,
      { input },
      'updateCardGroup'
    );
    return toAmplifyCardGroupShape<T>(result.updateCardGroup);
  }

  /**
   * SEE-1232 row h — BudgetSpendByMonth* reads. Each resolves what the Amplify read did:
   * the single record (or null/undefined) for the gets, the page's items for the lists.
   */
  async getBudgetSpendByMonth<T>(variables: { paidolId: string; yearAndMonth: string }): Promise<T | null> {
    const result = await this.execute<{ getBudgetSpendByMonth: T | null }>(
      getBudgetSpendByMonthQuery,
      variables,
      'getBudgetSpendByMonth'
    );
    return result.getBudgetSpendByMonth;
  }

  async getBudgetSpendByMonthAndPaymentCard<T>(variables: {
    paidolId: string;
    yearAndMonth: string;
    paymentCardId: string;
  }): Promise<T | null> {
    const result = await this.execute<{ getBudgetSpendByMonthAndPaymentCard: T | null }>(
      getBudgetSpendByMonthAndPaymentCardQuery,
      variables,
      'getBudgetSpendByMonthAndPaymentCard'
    );
    return result.getBudgetSpendByMonthAndPaymentCard;
  }

  // Resolves `undefined` when the connection is null, as the Amplify read's
  // `result.data?.listBudgetSpendByMonths?.items` did — getAverageMonthlySpend keys on it.
  async listBudgetSpendByMonths<T>(variables: BudgetSpendListArgs): Promise<T[] | undefined> {
    const result = await this.execute<{ listBudgetSpendByMonths: { items: T[] } | null }>(
      listBudgetSpendByMonthsQuery,
      variables,
      'listBudgetSpendByMonths'
    );
    return result.listBudgetSpendByMonths?.items;
  }

  async listBudgetSpendByMonthAndMerchantByAmount<T>(
    variables: BudgetSpendListArgs & { yearAndMonth: string }
  ): Promise<T[]> {
    const result = await this.execute<{ listBudgetSpendByMonthAndMerchantByAmount: { items: T[] } | null }>(
      listBudgetSpendByMonthAndMerchantByAmountQuery,
      variables,
      'listBudgetSpendByMonthAndMerchantByAmount'
    );
    return result.listBudgetSpendByMonthAndMerchantByAmount?.items ?? [];
  }

  async listBudgetSpendByMonthAndCategoryByAmount<T>(
    variables: BudgetSpendListArgs & { yearAndMonth: string }
  ): Promise<T[]> {
    const result = await this.execute<{ listBudgetSpendByMonthAndCategoryByAmount: { items: T[] } | null }>(
      listBudgetSpendByMonthAndCategoryByAmountQuery,
      variables,
      'listBudgetSpendByMonthAndCategoryByAmount'
    );
    return result.listBudgetSpendByMonthAndCategoryByAmount?.items ?? [];
  }

  async listBudgetSpendByMonthAndJobCodeIdByAmount<T>(variables: {
    paidolId: string;
    yearAndMonth: string;
    jobCodeId: string;
  }): Promise<T[]> {
    const result = await this.execute<{ listBudgetSpendByMonthAndJobCodeIdByAmount: { items: T[] } | null }>(
      listBudgetSpendByMonthAndJobCodeIdByAmountQuery,
      variables,
      'listBudgetSpendByMonthAndJobCodeIdByAmount'
    );
    return result.listBudgetSpendByMonthAndJobCodeIdByAmount?.items ?? [];
  }

  async addMerchantSpendRule(
    variables: DeepPartial<MutationAddMerchantSpendRuleArgs>
  ): Promise<MerchantSpendRule> {
    const result = await this.execute<{ addMerchantSpendRule: MerchantSpendRule }>(
      addMerchantSpendRuleMutation,
      variables,
      'addMerchantSpendRule'
    );
    return result.addMerchantSpendRule;
  }

  async editMerchantSpendRule(
    variables: DeepPartial<MutationEditMerchantSpendRuleArgs>
  ): Promise<EditMerchantSpendRuleResult> {
    const result = await this.execute<{ editMerchantSpendRule: EditMerchantSpendRuleResult }>(
      editMerchantSpendRuleMutation,
      variables,
      'editMerchantSpendRule'
    );
    return result.editMerchantSpendRule;
  }

  async removeMerchantSpendRule(
    variables: DeepPartial<MutationRemoveMerchantSpendRuleArgs>
  ): Promise<BasicHttpResponse> {
    const result = await this.execute<{ removeMerchantSpendRule: BasicHttpResponse }>(
      removeMerchantSpendRuleMutation,
      variables,
      'removeMerchantSpendRule'
    );
    return result.removeMerchantSpendRule;
  }

  async lockUnlockPaymentCard(
    variables: DeepPartial<MutationLockUnlockPaymentCardArgs>
  ): Promise<LockUnlockPaymentCardPayload> {
    const result = await this.execute<{ lockUnlockPaymentCard: LockUnlockPaymentCardPayload }>(
      lockUnlockPaymentCardMutation,
      variables,
      'lockUnlockPaymentCard'
    );
    return result.lockUnlockPaymentCard;
  }

  // --- Private helpers ---

  private async execute<T>(
    query: string,
    variables: object,
    operationName: string,
    authMode: AuthMode = 'cognito'
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'card', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const body = JSON.stringify({ query, variables });
      const response = await this.fetchWithAuth(body, authMode);

      let json: { data?: T; errors?: Array<{ message: string }> };
      try {
        json = await response.json();
      } catch {
        throw new Error(`HTTP ${response.status}: non-JSON response`);
      }

      reportCdkRefusal(json?.errors, { authMode, operationName });

      if (!response.ok) {
        const message =
          json?.errors?.map((e: { message: string }) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors.map((e: { message: string }) => e.message).join(', ');
        // Preserve the `{ errors: [{ message, errorType }] }` shape that middleware.ts and
        // handleHNErrors key on; a bare Error drops it and consumers fall through to the
        // generic "unknown error" instead of surfacing the real backend/Highnote message.
        throw Object.assign(new Error(`GraphQL errors: ${errorMessage}`), { errors: json.errors });
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        captureException(error, {
          tags: { area: 'card', type: isNetworkTransportError(error) ? 'network-transport' : 'graphql' },
          extra: {
            operation: operationName,
            endpoint: this.cdkAppSyncEndpoint,
          },
        });
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  private async fetchWithAuth(body: string, authMode: AuthMode): Promise<Response> {
    if (authMode === 'iam') {
      return this.executeWithIamAuth(body);
    }

    try {
      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();
      // retries: 0 — card issuance/assignment is not idempotent and carries no
      // client token, so a request that landed but lost its response must not be
      // re-sent. Still classifies/tags transport failures (SEE-1424 review).
      return fetchWithTransportRetry(
        this.cdkAppSyncEndpoint,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: jwt,
          },
          body,
        },
        { retries: 0 }
      );
    } catch (err) {
      // Surface non-"no session" failures (network, refresh-token, misconfig)
      // rather than silently downgrading an authenticated user to IAM scope.
      if (!isNoCognitoSessionError(err)) {
        captureException(err, {
          tags: { area: 'card', type: 'auth' },
          extra: { reason: 'currentSession-failed-but-not-no-session' },
        });
      }
      throw err;
    }
  }

  private async executeWithIamAuth(body: string): Promise<Response> {
    const credentials = await Auth.currentCredentials();
    const { accessKeyId, secretAccessKey, sessionToken } = credentials;

    const url = new URL(this.cdkAppSyncEndpoint);
    // Extract region from AppSync endpoint: *.appsync-api.{region}.amazonaws.com
    const regionMatch = url.hostname.match(/\.appsync-api\.([^.]+)\./);
    const region = regionMatch?.[1] ?? 'us-east-2';

    const request = {
      method: 'POST',
      url: this.cdkAppSyncEndpoint,
      headers: {
        'content-type': 'application/json',
        host: url.host,
      },
      data: body,
    };

    const signedRequest = Signer.sign(
      request,
      { access_key: accessKeyId, secret_key: secretAccessKey, session_token: sessionToken },
      { region, service: 'appsync' }
    );

    // retries: 0 — see fetchWithAuth: non-idempotent card mutations must stay single-shot.
    return fetchWithTransportRetry(
      this.cdkAppSyncEndpoint,
      {
        method: 'POST',
        headers: signedRequest.headers,
        body,
      },
      { retries: 0 }
    );
  }
}

// Export a singleton instance for convenience
export const cardService = new CardService();
